import type {HealthItemOwnProps} from '../components/App/Header/HealthItem/HealthItem.types'
import type {Branch} from '../services/rest'
import type {SubscriptionID} from '../utils/subscriptionId'

import type {
  AgentId,
  AgentPoolId,
  BuildId,
  BuildTypeId,
  ChangeId,
  KillOperationKind,
  ProblemId,
  ProjectId,
  ProjectInternalId,
  SakuraUIEnabledEnum,
  TestId,
} from '.'

type FilterOptions = {}
type Popup = {
  hidePopup(delay?: number, force?: boolean): unknown
  stopHidingPopup(): unknown
}
export type StatusPopup = Popup & {
  showPopup(
    buildTypeId: BuildTypeId,
    finished: boolean,
    skipChangesArtifacts: boolean,
    element: EventTarget,
    options?: {
      zIndex: number
    },
  ): unknown
}
type PopupOptions = {
  shift?: {
    dy?: number
  }
}
export type PeriodicalExecutor = {
  start: () => unknown
  unscheduledExecution: () => Promise<unknown>
  stop: () => unknown
}
export type RunCustomBuildStringParams = {
  branchName?: string | null | undefined
  stateKey?: string
  redirectTo?: string
  modificationId?: string
  init?: string | null | undefined
  initFromBuild?: string | null | undefined
  dependOnPromotionIds?: string
}
type RunCustomBuildParams = RunCustomBuildStringParams & {
  redirectToQueuedBuild?: boolean | null | undefined
  beforeTrigger?: () => unknown
  onError?: (error: Error) => unknown
  onSuccess?: (itemId: BuildId | null) => unknown
  afterShowDialog?: () => unknown
  isCustomRunDialogForRunButton?: boolean
}
export type InternalProps = {
  'teamcity.cloudProfiles.hiddenCloudTypes': string
  'teamcity.hidden.artifacts.use.extended.names': boolean
  'teamcity.internal.agent.distribution.jdk.bundle.enabled': boolean
  'teamcity.internal.buildChangesPopupLimit': number
  'teamcity.internal.nightly': boolean
  'teamcity.overviewPage.buildTypes.limit': number
  'teamcity.pipelines.agentPage.enabled': boolean
  'teamcity.pipelines.customAgentsNames': string
  'teamcity.pipelines.dependencyCache.enabled': boolean
  'teamcity.pipelines.enabled': boolean
  'teamcity.pipelines.feature.notifications.enabled': boolean
  'teamcity.pipelines.feature.pipeline.notifications.enabled': boolean
  'teamcity.pipelines.feature.smoothParameterReplacement.enabled': boolean
  'teamcity.pipelines.interviewRecruitmentBanner.additionalMessage': string
  'teamcity.pipelines.interviewRecruitmentBanner.buttonName': string
  'teamcity.pipelines.interviewRecruitmentBanner.enabled': boolean
  'teamcity.pipelines.feature.compatibilityAgents.enabled': boolean
  'teamcity.pipelines.interviewRecruitmentBanner.link': string
  'teamcity.pipelines.interviewRecruitmentBanner.mainMessage': string
  'teamcity.pipelines.interviewRecruitmentPopup.enabled': boolean
  'teamcity.pipelines.jobCountHardLimit': number
  'teamcity.pipelines.jobCountSoftLimit': number
  'teamcity.pipelines.muteInvestigateButton.enabled': boolean
  'teamcity.pipelines.projects.enabled': boolean
  'teamcity.pipelines.testNpmConnection.enabled': boolean
  'teamcity.pipelines.unity.enabled': boolean
  'teamcity.pipelines.statusPublishing.configuration.enabled': boolean
  'teamcity.pipelines.feature.withoutVcs.enabled': boolean
  'teamcity.pipelines.welcomePopup.enabled': boolean
  'teamcity.pipelines.earlyAccessLabel.enabled': boolean
  'teamcity.pipelines.classicUIBanner.enabled': boolean
  'teamcity.pipelines.pipelineRunClassicRedirect.enabled': boolean
  'teamcity.pipelines.feature.pullRequests.enabled': boolean
  'teamcity.pipelines.buildFeatures.enabled': boolean
  'teamcity.recipes.public.enabled': boolean
  'teamcity.recipes.public.marketplace.url': string
  'teamcity.ui.agentPoolsFavoriteLimit': number
  'teamcity.ui.agentDataUpdateTimeout': number
  'teamcity.ui.agentPreviewsUpdateTimeout': number
  'teamcity.ui.buildPage.headerActions.enabled': boolean
  'teamcity.ui.buildStepSelector.enabled': boolean
  'teamcity.ui.buildStepSelector.privateRecipeList.pageSize': number
  'teamcity.ui.buildStepSelector.publicRecipeList.pageSize': number
  'teamcity.ui.compositeBuildsOnTimeline.enabled': boolean
  'teamcity.ui.customWelcomeTitle': string
  'teamcity.ui.darkTheme.adapter.enabled': boolean
  'teamcity.ui.darkTheme.allowed': boolean
  'teamcity.ui.experimental': SakuraUIEnabledEnum
  'teamcity.ui.newAgentCompatibleConfigurationsTab': boolean
  'teamcity.ui.newInvestigationsPage': boolean
  'teamcity.ui.requestPoolSize': number
  'teamcity.ui.sakura.releaseBanner.enabled': boolean
  'teamcity.ui.sakuraInvestigationsTab': boolean
  'teamcity.ui.sakuraProblemsTab': boolean
  'teamcity.ui.showAuthAgentDialog': boolean
  'teamcity.ui.showDownloadAllArtifactsLink': boolean
  'teamcity.ui.showLicenseAgreementToAllUsers': boolean
  'teamcity.ui.showObligatoryHints': boolean
  'teamcity.ui.sidebar.saveSearchState': boolean
  'teamcity.ui.sidebar.skipWSEventsSubscription': boolean
  'teamcity.ui.statuses.throttleInterval': number
  'teamcity.ui.subscriptions.debounceInterval': number
  'teamcity.ui.useServiceWorkers': boolean
  'teamcity.pipelines.runOn.enabled': boolean
  'teamcity.pipelines.feedbackForm.enabled': boolean
  'teamcity.ui.createProject.newui.enabled': boolean
  'teamcity.ui.createBuildType.newui.enabled': boolean
  'teamcity.ui.createConnection.newui.enabled': boolean
  'teamcity.pipelines.tryPipelinesLink.enabled': boolean
  'teamcity.pipelines.integrations.newui.enabled': boolean
  'teamcity.pipelines.parameterInheritance.enabled': boolean
  'teamcity.ai.assistant.grazietoken': string
  'teamcity.ui.stopAutocompleteForPrivateDockerIntegrations.enabled': boolean
  'teamcity.pipelines.feature.testDriveJob': boolean
  'teamcity.cloud.enabled': boolean
  'teamcity.ui.dormOnly.enabled': boolean
  'teamcity.feedback.csat.enabled': boolean
  'teamcity.feedback.csat.testing.delayInSecondsToShow': number
  'teamcity.ui.chainPanning.enabled': boolean
  'teamcity.pipelines.buildChains.enabled': boolean
  'teamcity.pipelines.buildChains.view.enabled': boolean
  'teamcity.pipelines.buildChains.snapshotDependencies.downstream.enabled': boolean
  'teamcity.pipelines.creation.enabled': boolean
  'teamcity.pipelines.creation.prohibitedVcsTypes': string
  'teamcity.pipelines.runActions.enabled': boolean
  'teamcity.pipelines.allowedRunners': string
  'teamcity.properties.automaticallyMarkImportantBuildsAsFavorite.enabled': boolean
  'teamcity.ui.fromAnyGitURL.enabled': boolean
  'teamcity.pipelines.runCustomActions.enabled': boolean
  'teamcity.pipelines.lastRun.enabled': boolean
  'teamcity.pipelines.overview.enabled': boolean
  'teamcity.pipelines.kotlinDSL.enabled': boolean
  'teamcity.pipelines.outputParameters.enabled': boolean
  'teamcity.pipelines.addRepository.existingVcsRoot.enabled': boolean
  'teamcity.pipelines.creationFlow.pipelineFirst.enabled': boolean
}

declare global {
  interface SubscriptionManager {
    subscribe(
      topicId: string,
      onMessage: (arg0: string) => unknown,
      subscriptionId?: SubscriptionID,
    ): () => void
    unsubscribe(topicId: string, subscriptionId?: SubscriptionID): unknown
  }

  interface ServerLink {
    isConnectionAvailable: () => boolean
  }

  interface BS {
    helpUrlPrefix?: string
    publicKey?: string
    ajaxRequest?: (url: string, params: unknown) => unknown
    reload?: (reload: boolean) => unknown
    Activator?: {
      doOpen(command: string): unknown
    }
    AdminActions?: {
      setHealthItemVisibility: (
        type: string,
        categoryId: string,
        itemId: string,
        visible: boolean,
        applyGlobally: boolean,
        oncomplete: () => unknown,
      ) => unknown
    }
    Agent?: {
      showChangeStatusDialog(
        enable: boolean,
        id: AgentId,
        registered: boolean,
        actionCode: 'changeAuthorizeStatus' | 'changeAgentStatus',
        poolOptions?: {
          poolId?: AgentPoolId
          cloud?: boolean
        },
        updateStatus?: () => unknown,
      ): unknown
    }
    AgentsReact?: {
      refreshTabsCounters(): unknown
    }
    BuildResultsPopupTracker?: (buildId: BuildId) => StatusPopup
    PromoDetailsPopupFunc?: (buildId: BuildId) => StatusPopup
    BuildCommentDialog?: {
      showBuildCommentDialog(
        buidId: BuildId,
        comment?: string | null | undefined,
        doNotReload?: boolean,
      ): Promise<string>
    }
    ChangesPopup?: Popup & {
      showBuildChangesPopup(nearestElement: HTMLElement, id: BuildId, isBuildId?: boolean): unknown
      showPendingChangesPopup(
        nearestElement: HTMLElement,
        buildTypeId: BuildTypeId,
        branch?: string | null | undefined,
        options?: PopupOptions,
      ): unknown
    }
    FilesPopup?: Popup & {
      showPopup(
        event: Event,
        options: {
          parameters: {
            modId: ChangeId
            buildTypeId: BuildTypeId
            personal: boolean
            chain?: boolean
          }
        },
      ): unknown
    }
    DependentArtifactsPopup?: Popup & {
      showPopup(
        nearestElement: EventTarget,
        buildId: BuildId,
        targetBuildId: BuildId,
        mode: string,
      ): unknown
    }
    AgentInfoPopup?: Popup & {
      showAgentsPopup(nearestElement: EventTarget, promoId: BuildId, tab?: string): unknown
    }
    BuildCommentPopup?: Popup & {
      showBuildCommentPopup(nearestElement: HTMLElement, parametersAsString: string): unknown
    }
    LazyTree?: {
      treeUrl: string
      ignoreHashes: boolean
      options: unknown
      loadTree(elementId: string): unknown
    }
    Logout?: (url: string) => void
    PinBuildDialog?: {
      showPinBuildDialog(
        buildId: BuildId,
        pin: boolean,
        partOfChain: boolean,
        defaultMessage: string | null | undefined,
        availableTagsContainerId: string,
        doNotReload?: boolean,
        buildTypeId?: BuildTypeId,
      ): Promise<boolean>
    }
    Popup?: {
      container: HTMLElement | null | undefined
    }
    Tags?: {
      showEditDialog(
        promotionId: BuildId,
        escapedPublicTags: string,
        escapedPrivateTags: string,
        partOfChain: boolean,
        availableTagsContainerId: string,
        preventReload?: boolean,
        buildTypeId?: BuildTypeId,
      ): Promise<Array<string>>
    }
    Util?: {
      showHelp(
        event: Event,
        url: string,
        options?: {
          width?: number | null | undefined
          height?: number | null | undefined
          preservePlus?: boolean
        },
      ): unknown
      popupWindow(
        url: string,
        target?: string,
        options?: {
          width?: number
          height?: number
          safe?: boolean
          opener?: Window | null
        },
      ): Window | null
    }
    TestMetadata?: {
      showGraph(
        anchor: EventTarget,
        testNameId: TestId,
        buildId: BuildId,
        escapedName: string,
        buildTypeId: BuildTypeId,
      ): unknown
    }
    RunBuild?: {
      runCustomBuild(
        buildTypeId: BuildTypeId,
        options: RunCustomBuildParams,
      ): Promise<BuildId | null>
    }
    RunBuildDialog?: {
      showTab: (tabId: string) => unknown
      disableSubmit: () => unknown
      enableSubmit: () => unknown
    }
    ResponsibilityDialog?: {
      showDialog(
        buildTypeId: BuildTypeId,
        buildTypeFullName: string,
        presetFix: boolean,
        withoutReload?: boolean,
        submitHandler?: () => unknown,
      ): unknown
    }
    EditModificationDescriptionDialog?: {
      showDialog(): unknown
    }
    BulkInvestigateMuteTestDialog?: {
      show(
        testsData: Readonly<Record<string, void | string>>,
        fixMode: boolean,
        flakyTestIds: Readonly<Record<string, void | boolean>>,
        withoutReload?: boolean,
        submitHandler?: () => unknown,
      ): unknown
      showForBuildProblem(
        problemId: ProblemId,
        buildId: BuildId,
        fixMode: boolean,
        withoutReload?: boolean,
        submitHandler?: () => unknown,
      ): unknown
      showForBuildProblems(
        problemsData: Readonly<Record<string, void | string>>,
        fixMode: boolean,
        withoutReload?: boolean,
        submitHandler?: () => unknown,
      ): unknown
    }
    enableJQueryDropDownFilter?: (selectId: string, filterOptions: FilterOptions) => unknown
    StopBuildDialog?:
      | {
          showStopBuildDialog: (
            promoIds: ReadonlyArray<BuildId>,
            defaultMessage: string,
            operationKind: KillOperationKind,
            reStopMessage?: (string | HTMLElement) | null | undefined,
          ) => Promise<boolean>
        }
      | null
      | undefined
    RestProjectsPopup?:
      | {
          height: () => unknown
        }
      | null
      | undefined
    Hider?: {
      _currentZindex: () => number
      hasVisiblePopups: () => boolean
    }
    blockRefreshPermanently?: (arg0: string | null | undefined) => unknown
    unblockRefresh?: (arg0: string | null | undefined) => unknown
    Branch?: {
      WILDCARD_NAME: string
      parseBranch: (arg0: string | null | undefined) => Branch | null
      stringifyBranch: (arg0: Branch | null | undefined) => string
      registerProjectBranch: (arg0: ProjectId, arg1: string | null | undefined) => unknown
      saveBranch: (arg0: ProjectId, arg1: string | null | undefined) => unknown
      getBranch: (projectId: ProjectId, noStorage?: boolean) => string | null
    }
    periodicalExecutor?: (job: () => Promise<unknown>, arg1: number) => PeriodicalExecutor
    PauseBuildTypeDialog?: {
      showPauseBuildTypeDialog: (
        buildTypeId: BuildTypeId,
        pause: boolean,
        hasPermissions: boolean,
        defaultMessage: string | null | undefined,
        noReload: boolean,
      ) => Promise<void>
    }
    VcsUsername?: {
      addVcsNameFromModification(id: ChangeId): void
    }
    selectRunnerInFlattenMode?: (key: string, onLoadingComplete: () => void) => void
    openRunnerSelector?: (event: any) => void
    updateRunnerContainer?: (key?: string) => void
    EditBuildRunnerForm?: {
      disable: () => void
      enable: () => void
      clearErrors: () => void
      setModified: (modified: boolean) => void
      removeUpdateStateHandlers: () => void
    }
    GitHub?: {
      repositories?: Record<
        string,
        {
          url: string | null | undefined
          isPrivate: boolean | null | undefined
          owner: string | null | undefined
          name: string | null | undefined
        }
      >
    }
    ChangeLogGraph?: {
      setGraphData: (graph: unknown) => Promise<void>
      initGraph: (params: {
        graphContainerSelector: string
        graphToggleSelector?: string
        changesTableSelector: string
        isSakuraUI?: boolean
      }) => void
      redrawGraph: () => void
      restoreDefaultGraphView: () => void
    }
    BuildFeatureDialog?: {
      close: () => void
      setSaveButtonDisabled: (state: boolean) => void
      getSaveButton: () => HTMLInputElement
      recenterDialog: () => void
    }
    EditDsl?: {
      setCustomLoadDslFragment: <T extends (...args: any[]) => Promise<void>>(callback: T) => void
      customLoadDslFragment: <T extends any[]>(..._: T) => Promise<void>
    }
    globalHealthItems?: readonly HealthItemOwnProps[]
    OAuthConnectionForm?: {
      save: (onSuccess?: () => void) => void
      onConnectionCreated?: (message?: string) => void
    }
    AdminNavigation?: {projectAdminPages: string[]}
  }

  interface Window {
    _tc_es: (arg0: Event) => unknown
    internalProps: InternalProps
    GitHubRepositoriesContentUpdater?: () => void
    Intl: {
      /**
       * reason: remove in the future
       * Due to: TS is not supported yet
       * link: https://github.com/microsoft/TypeScript/issues/49231
       */
      supportedValuesOf: (value: string) => string[]
    }
    CloudReactUISettings?: {
      accountProjectId: ProjectInternalId
      accountProjectExternalId: ProjectId
    }
  }
}

export const _tc_es = window._tc_es
export const BS = window.BS
export const internalProps = window.internalProps
